import React, { Suspense } from "react";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useHistory, useLocation } from "react-router-dom";
import { Routes } from "routes";
import { BrowserRouter as Router } from "react-router-dom";
import {
  api,
  authApi,
  passThruInterceptor,
  retryInterceptor,
  missingPaymentInterceptor,
  errorInterceptor,
} from "api-bootstrap";
import ProvideAuth from "providers/ProvideAuth";
import ProvideApi from "providers/ProvideApi";
import ProvideBranding from "providers/ProvideBranding";
import ProvideContext from "providers/ProvideContext";
import ProvideAchievements from "providers/ProvideAchievements";
import {
  ProvideAlerts,
  Alerts,
  useAlert,
} from "advancement-solutions-components/dist/providers";

import {
  ProvideAnnouncements,
  Announements,
  useAnnouncement,
} from "providers/ProvideAnnouncements";

import { PageLoadingSkeleton } from "advancement-solutions-components/dist/components";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import useMediaQuery from "@mui/material/useMediaQuery";
import { modeTheme } from "./theme";
import useWindowSize from "hooks/useWindowSize";
import Confetti from "react-confetti";
import { useAppContext } from "providers/ProvideContext";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const Bootstrap = () => {
  return (
    <Router>
      <Suspense fallback={<PageLoadingSkeleton />}>
        <StyledEngineProvider injectFirst>
          <ProvideContext>
            <Themer />
          </ProvideContext>
        </StyledEngineProvider>
      </Suspense>
    </Router>
  );
};

const Themer = () => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const mode = prefersDarkMode ? "dark" : "light";
  const theme = React.useMemo(() => modeTheme(mode), [mode]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ProvideAlerts>
        <ProvideAnnouncements>
          <App />
        </ProvideAnnouncements>
      </ProvideAlerts>
    </ThemeProvider>
  );
};

const App = () => {
  const history = useHistory();
  const location = useLocation();
  const { send } = useAlert();
  const { width, height } = useWindowSize();
  const { showCelebrate, setShowCelebrate } = useAppContext();

  authApi.interceptors.response.use(
    passThruInterceptor(send),
    missingPaymentInterceptor(history, send)
  );
  authApi.interceptors.response.use(
    passThruInterceptor(send),
    retryInterceptor(api, location, history, send)
  );
  authApi.interceptors.response.use(
    passThruInterceptor(send),
    errorInterceptor(location, history, send)
  );

  return (
    <ProvideBranding brand={process.env.REACT_APP_BRAND}>
      <Elements stripe={stripePromise}>
        <ProvideApi>
          <ProvideAchievements>
            <ProvideAuth>
              <Routes />
            </ProvideAuth>
          </ProvideAchievements>
        </ProvideApi>
      </Elements>
      <Alerts />
      <Announements celebrate={setShowCelebrate} />
      {showCelebrate && (
        <Confetti
          width={width}
          height={height}
          numberOfPieces={300}
          recycle={false}
        />
      )}
    </ProvideBranding>
  );
};

export default Bootstrap;
